import React, { useMemo } from 'react';

import cx from 'classnames';
import { equals, pick } from 'ramda';
import { Field, Form } from 'react-final-form';
import { useSelector } from 'react-redux';

import FormSection from 'components/Layouts/Form/FormSection';
import { FilesAdapter as DefaultFilesAdapter } from 'components/Pages/Admin/PageTypes/Property/Resources/Upload/FilesAdapter';
import ArrayFields from 'components/shared/ArrayFields';
import Button from 'components/shared/Button';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import Loader from 'components/shared/Loader';
import Text from 'components/shared/Typography/Text';
import UnsavedChangesFormWatcher from 'components/shared/UnsavedChangesFormWatcher';
import useElementBottomPosition, { SNACKBAR_POSITION } from 'hooks/useElementBottomPosition';
import { useScrollPageToTop } from 'hooks/useScrollPageToTop';
import useValidationSchema from 'hooks/useValidationSchema';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { scrollToErrorOnSubmitDecorator } from 'utils/form/decorators';
import isNoData from 'utils/isNoData';
import {
  useItinerariesItemNextStepFormHandler,
  useItinerariesItemSaveHandler,
} from 'utils/itineraries/functions/builderUtils';

import LayoutWithFooter from '../../../../Layouts/shared/LayoutWithFooter';
import ItinerariesItemActionButtons from '../../ItinerariesItemBuilder/ActionButtons';
import { BuilderFooterButton } from '../../PageTypes/shared/BuilderFooterButton';
import { GUEST_PORTAL_ASSET_RECOMMENDED_MAX_SIZE, useAssetsHandlers } from '../shared/functions';
import {
  FieldValuesKeys,
  TravelWalletDocumentTypes,
  TravelWalletFileKeys,
  TravelWalletContentKeys,
} from './constansts';
import s from './index.module.css';
import validationSchema from './validationSchema';

export const isTravelWalletEmpty = (guestPortal) => {
  return Object.values(TravelWalletContentKeys).every((key) => isNoData(guestPortal[key]));
};

export default function TravelWallet() {
  useScrollPageToTop();
  useElementBottomPosition(SNACKBAR_POSITION);
  const [isSaving, setIsSaving] = React.useState(false);

  const handleNextStep = useItinerariesItemNextStepFormHandler();
  const handleSave = useItinerariesItemSaveHandler();
  const guestPortal = useSelector(itinerariesItemDataSelector);

  const initialFormValues = useMemo(() => pick(FieldValuesKeys, guestPortal), [guestPortal]);
  const validate = useValidationSchema(validationSchema);
  const handleValues = (values) => ({
    ...values,
    additionalLinks: (values?.additionalLinks || []).map((link) => ({ ...link, link: link?.link?.trim() })),
  });

  return (
    <LayoutWithFooter>
      {({ footerComponent: FooterComponent }) => (
        <Form
          onSubmit={(values) => handleNextStep(handleValues(values))}
          initialValues={initialFormValues}
          validate={validate}
          decorators={[scrollToErrorOnSubmitDecorator]}
        >
          {({ handleSubmit, submitting, values, form }) => {
            return (
              <form onSubmit={handleSubmit} autoComplete="nope">
                <UnsavedChangesFormWatcher />
                {submitting && <Loader isFillBlock />}

                <FormSection type="empty">
                  <Text type="brandon" size="large" isStrong weight="500">
                    Travel Wallet
                  </Text>
                </FormSection>

                <FormSection type="rounded">
                  <Field
                    name={TravelWalletFileKeys.vouchersFiles}
                    component={({ input }) => <FilesAdapter input={input} form={form} title="Vouchers" />}
                    isEqual={equals}
                  />
                </FormSection>

                <FormSection type="rounded">
                  <Field
                    name={TravelWalletFileKeys.eTicketsFiles}
                    component={({ input }) => <FilesAdapter input={input} form={form} title="E-Tickets" />}
                    isEqual={equals}
                  />
                </FormSection>

                <FormSection type="rounded">
                  <Field
                    name={TravelWalletFileKeys.waiversFiles}
                    component={({ input }) => <FilesAdapter input={input} form={form} title="Waivers" />}
                    isEqual={equals}
                  />
                </FormSection>

                <FormSection type="rounded">
                  <Field
                    name={TravelWalletFileKeys.miscFiles}
                    component={({ input }) => <FilesAdapter input={input} form={form} title="Miscellaneous" />}
                    isEqual={equals}
                  />
                </FormSection>

                <FormSection type="rounded">
                  <Text type="brandon" isStrong weight="500" className={s.linksTitle}>
                    Additional Links
                  </Text>

                  <div className={s.linksWrapper}>
                    <ArrayFields
                      initialValues={initialFormValues}
                      data={{
                        sectionName: 'additionalLinks',
                        config: { twoColumn: true },
                        fieldsData: [
                          { name: 'name', placeholder: 'Link Name' },
                          { name: 'link', placeholder: 'Link' },
                        ],
                      }}
                    />
                  </div>
                </FormSection>

                <FooterComponent>
                  <FormActions isRight>
                    <BuilderFooterButton disabled={submitting || isSaving} onClick={form.submit}>
                      Next
                    </BuilderFooterButton>
                  </FormActions>
                </FooterComponent>

                <ItinerariesItemActionButtons
                  submitting={submitting}
                  onSave={() => {
                    form.getRegisteredFields().forEach((field) => form.blur(field)); // trigger validation
                    if (form.getState().hasValidationErrors) {
                      return form.submit(); // trigger scrollToErrorOnSubmitDecorator
                    }

                    handleSave(handleValues(values));
                  }}
                  onIsSavingChange={setIsSaving}
                />
              </form>
            );
          }}
        </Form>
      )}
    </LayoutWithFooter>
  );
}

const FilesAdapter = ({ input, form, title }) => {
  const { name, value, onChange } = input;
  const fileCategory = TravelWalletDocumentTypes?.[name];
  const { handleDeleteAssets } = useAssetsHandlers();

  const hasFiles = value?.length > 0;

  return (
    <DefaultFilesAdapter
      withEmpty={false}
      fileCategory={fileCategory}
      value={value}
      onChange={onChange}
      contentWrapperClassName={cx(s.filesAdapterContentWrapper, !hasFiles && s.filesAdapterContentWrapperEmpty)}
      recommendedFileSize={GUEST_PORTAL_ASSET_RECOMMENDED_MAX_SIZE}
      renderHeader={(onUpload) => (
        <div className={s.sectionHeader}>
          <Text type="brandon" isStrong weight="500">
            {title}
          </Text>

          {hasFiles ? (
            <Button
              htmlType="button"
              type="default"
              isWidthFitContent
              className={s.sectionHeaderButton}
              onClick={() => {
                handleDeleteAssets({
                  fieldNames: [name],
                  form,
                });
              }}
            >
              Clear
            </Button>
          ) : (
            <Button
              htmlType="button"
              type="default"
              isWidthFitContent
              className={s.sectionHeaderButton}
              onClick={onUpload}
            >
              Upload
            </Button>
          )}
        </div>
      )}
      withDnd
    />
  );
};
